import { useContext, type ReactNode } from 'react'

import { Text } from '@wartek-id/text-v1'

import {
  Table as KinerjaTable,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import {
  PERIODIK_PERFORMANCE_PRACTICE_RESULTS,
  PERIODIK_WORK_BEHAVIOR_RESULTS,
} from 'app/KinerjaShared/constants/periodik'
import { TableContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/TableContext'
import { PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS } from 'app/KinerjaTimPenilaiPSPage/constants/penetapan'

type TableProps = {
  nextPageResolver: (pengawasSekolahId: string) => string
  toolbar?: ReactNode
  tableId?: string
}
export function TableSendToEkin({ toolbar, tableId }: TableProps) {
  const context = useContext(TableContext)

  // Ensure that the component is used within the correct context provider
  if (!context)
    throw new Error(
      '<TablePegawaiPenetapan.Table> must be used within a <TablePegawaiPenetapan.Provider>'
    )

  // ------------------------------------
  // Get context data and its derivatives
  // ------------------------------------
  const { pengawasSekolahList, isFetching } = context
  const isEmpty = pengawasSekolahList.length === 0

  return (
    <div className="bg-white rounded-lg">
      {toolbar && (
        <div className="p-6">
          <div className="flex justify-between">{toolbar}</div>
        </div>
      )}
      <KinerjaTable rounded className="bg-white" id={tableId}>
        <TableHeader className="!rounded-t-lg !bg-surface-subdued">
          <TableHeaderItem className="text-sm">Nama</TableHeaderItem>
          <TableHeaderItem className="text-sm">Instansi</TableHeaderItem>
          <TableHeaderItem className="text-sm">
            Rating Praktik Kinerja
          </TableHeaderItem>
          <TableHeaderItem className="text-sm">
            Rating Perilaku Kerja
          </TableHeaderItem>
          <TableHeaderItem className="w-56 text-sm">
            Predikat Kinerja
          </TableHeaderItem>
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableShimmer columns={9} rows={10} />
          ) : isEmpty ? (
            <TableRowStates colSpan={9} type="EMPTY">
              <Text color="subdued">
                Belum ada data pegawai pada Penetapan Predikat Kinerja ini.
              </Text>
            </TableRowStates>
          ) : (
            <>
              {pengawasSekolahList.map((item, i) => {
                const { predikatKinerjaTk, predikatKinerjaFinal } = item

                // Extract values for each key, preferring 'final' if defined and not 'UNDEFINED', else fallback to 'tk'
                const predikatKinerja =
                  predikatKinerjaFinal?.predikatKinerja !== 'UNDEFINED'
                    ? predikatKinerjaFinal?.predikatKinerja
                    : predikatKinerjaTk?.predikatKinerja !== 'UNDEFINED'
                    ? predikatKinerjaTk?.predikatKinerja
                    : ''
                const praktikKinerjaRating =
                  predikatKinerjaFinal?.praktikKinerjaRating !== 'UNDEFINED'
                    ? predikatKinerjaFinal?.praktikKinerjaRating
                    : predikatKinerjaTk?.praktikKinerjaRating !== 'UNDEFINED'
                    ? predikatKinerjaTk?.praktikKinerjaRating
                    : ''
                const perilakuKerjaRating =
                  predikatKinerjaFinal?.perilakuKerjaRating !== 'UNDEFINED'
                    ? predikatKinerjaFinal?.perilakuKerjaRating
                    : predikatKinerjaTk?.perilakuKerjaRating !== 'UNDEFINED'
                    ? predikatKinerjaTk?.perilakuKerjaRating
                    : ''

                return (
                  <TableRow
                    key={item.pengawasSekolahId + i}
                    className="font-semibold align-middle"
                  >
                    <TableRowItem>
                      <div className="overflow-wrap-anywhere">
                        <Text variant="body-sm">{item.namaPengawas}</Text>
                      </div>
                    </TableRowItem>
                    <TableRowItem className="overflow-wrap-anywhere !font-normal">
                      <Text variant="body-sm">
                        {item.dinasPendidikan ?? '-'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text
                        as="span"
                        variant="body-sm"
                        className="lg:text-nowrap"
                      >
                        {PERIODIK_PERFORMANCE_PRACTICE_RESULTS?.[
                          praktikKinerjaRating
                        ] || '-'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      <Text
                        as="span"
                        variant="body-sm"
                        className="lg:text-nowrap"
                      >
                        {PERIODIK_WORK_BEHAVIOR_RESULTS?.[
                          perilakuKerjaRating
                        ] || '-'}
                      </Text>
                    </TableRowItem>
                    <TableRowItem>
                      {PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS?.[
                        predikatKinerja
                      ] || '-'}
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </KinerjaTable>
    </div>
  )
}
